const formatters = new Map<string, Intl.DateTimeFormat>();
const clockParts = new Map<string, Record<string, string>>();
const instantCache = new Map<string, string>();
function formatter(timeZone: string) {
  if (!formatters.has(timeZone))
    formatters.set(
      timeZone,
      new Intl.DateTimeFormat("sv-SE", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
      }),
    );
  return formatters.get(timeZone)!;
}
function parts(value: string | number | Date, timeZone: string) {
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime()))
    throw new Error("Enter a valid date and time.");
  const key = `${date.getTime()}:${timeZone}`;
  const cached = clockParts.get(key);
  if (cached) return cached;
  const result = Object.fromEntries(
    formatter(timeZone)
      .formatToParts(date)
      .filter((item) => item.type !== "literal")
      .map((item) => [item.type, item.value]),
  );
  if (clockParts.size > 50000) clockParts.clear();
  clockParts.set(key, result);
  return result;
}
export function validDate(value: string): boolean {
  return (
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    Number.isFinite(Date.parse(`${value}T12:00:00Z`)) &&
    new Date(`${value}T12:00:00Z`).toISOString().slice(0, 10) === value
  );
}
export const validTime = (value: string) =>
  /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
export function localDate(
  value: string | number | Date,
  timeZone = "Europe/Helsinki",
): string {
  const p = parts(value, timeZone);
  return `${p.year}-${p.month}-${p.day}`;
}
export function localTime(
  value: string | number | Date,
  timeZone = "Europe/Helsinki",
): string {
  const p = parts(value, timeZone);
  return `${p.hour}:${p.minute}`;
}
export const operationalNow = (state?: { demoClockAt?: string }, explicitAt?: string): string => explicitAt ?? state?.demoClockAt ?? new Date().toISOString();
export const todayAt = (timeZone = "Europe/Helsinki", at?: string) =>
  localDate(at ?? new Date(), timeZone);
export function addDays(date: string, days: number): string {
  if (!validDate(date) || !Number.isSafeInteger(days))
    throw new Error("Enter a valid calendar date.");
  const value = new Date(`${date}T12:00:00Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}
export const daysBetween = (a: string, b: string) =>
  Math.round(
    (Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86400000,
  );
export const weekday = (date: string) =>
  new Date(`${date}T12:00:00Z`).getUTCDay();
export function monthDate(
  date: string,
  amount: number,
  monthDay: number,
  invalidDay: "skip" | "last-day",
): string | null {
  const value = new Date(`${date.slice(0, 7)}-01T12:00:00Z`);
  value.setUTCMonth(value.getUTCMonth() + amount);
  const last = new Date(
    Date.UTC(value.getUTCFullYear(), value.getUTCMonth() + 1, 0),
  ).getUTCDate();
  if (monthDay > last && invalidDay === "skip") return null;
  value.setUTCDate(Math.min(monthDay, last));
  return value.toISOString().slice(0, 10);
}
/** Resolve a local scheduling intent; ambiguous/nonexistent clock times never silently shift. */
export function localToInstant(
  date: string,
  time: string,
  timeZone = "Europe/Helsinki",
  nextDay = false,
  disambiguation: "reject" | "earlier" | "later" = "reject",
): string {
  if (nextDay) date = addDays(date, 1);
  if (!validDate(date) || !validTime(time))
    throw new Error("Enter a valid local date and time.");
  const key = `${date}:${time}:${timeZone}:${disambiguation}`;
  const cached = instantCache.get(key);
  if (cached) return cached;
  const nominal = Date.parse(`${date}T${time}:00Z`);
  const offsets = new Set<number>();
  for (let step = -6; step <= 6; step++) {
    const instant = nominal + step * 6 * 3600000;
    const p = parts(instant, timeZone);
    const displayedAsUTC = Date.parse(
      `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}Z`,
    );
    offsets.add(displayedAsUTC - instant);
  }
  const candidates = [...offsets]
    .map((offset) => nominal - offset)
    .filter(
      (instant) =>
        localDate(instant, timeZone) === date &&
        localTime(instant, timeZone) === time,
    )
    .sort((a, b) => a - b);
  if (!candidates.length)
    throw new Error(
      "This local time does not exist because the clocks change. Choose another time.",
    );
  if (candidates.length > 1 && disambiguation === "reject")
    throw new Error(
      "This local time occurs twice because the clocks change. Choose the earlier or later occurrence.",
    );
  const result = new Date(
    disambiguation === "later"
      ? candidates[candidates.length - 1]
      : candidates[0],
  ).toISOString();
  if (instantCache.size > 20000) instantCache.clear();
  instantCache.set(key, result);
  return result;
}
export const durationMinutes = (start: string, end: string) =>
  Math.max(0, (Date.parse(end) - Date.parse(start)) / 60000);
export const overlap = (
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string,
) => aStart < bEnd && aEnd > bStart;
export function intervalMinutes(
  start: string,
  end: string,
  intervals: { start: string; end: string }[],
): number {
  const clipped = intervals
    .filter((item) => overlap(start, end, item.start, item.end))
    .map((item) => ({
      start: item.start > start ? item.start : start,
      end: item.end < end ? item.end : end,
    }))
    .sort((a, b) => a.start.localeCompare(b.start));
  const merged: { start: string; end: string }[] = [];
  for (const item of clipped) {
    const previous = merged[merged.length - 1];
    if (previous && item.start <= previous.end)
      previous.end = item.end > previous.end ? item.end : previous.end;
    else merged.push({ ...item });
  }
  return merged.reduce(
    (total, item) => total + durationMinutes(item.start, item.end),
    0,
  );
}
export function isoAddHours(instant: string, hours: number): string {
  return new Date(Date.parse(instant) + hours * 3600000).toISOString();
}
export function stableId(
  prefix: string,
  ...values: (string | number)[]
): string {
  let hash = 2166136261;
  for (const char of values.join("|"))
    hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  return `${prefix}-${(hash >>> 0).toString(36)}`;
}
